import { Product, ProductType, AccommodationType } from "app/shared/model/Product";
import { getOfferingsMinimal } from "app/shared/api/getOfferings";
import type { ProductOffering } from "app/shared/model/ProductOffering";
import type { SchoolOption } from "app/pages/DiscountEditor/modules/ProductSelection/state";

function canonicalProductTypeName(name: string): ProductType | undefined {
    const n = name.toLowerCase();
    if (n === "course") return ProductType.Course;
    if (n === "accommodation" || n === "accommodationroom") return ProductType.Accommodation;
    if (n === "addon" || n === "add-on" || n === "add-ons") return ProductType.AddOn;
    if (n === "transfer") return ProductType.Transfer;
    if (n === "package") return ProductType.Package;
    return undefined;
}

function productFromOffering(offering: ProductOffering): Product | undefined {
    const code = String(offering.productId || "").trim();
    const match = code.match(/^([A-Za-z]+)-(-?[0-9]+)$/);
    if (!match) return undefined;
    const type = canonicalProductTypeName(match[1]);
    if (!type) return undefined;

    const name = (offering.name || "").trim() || code;
    if (type === ProductType.Accommodation) {
        return {
            code,
            name,
            type: ProductType.Accommodation,
            active: true,
            accommodationType: accommodationTypeFromName(name),
        };
    }
    return {
        code,
        name,
        type,
        active: true,
    } as Product;
}

function accommodationTypeFromName(name: string): AccommodationType | undefined {
    const nameLower = name.toLowerCase();
    if (nameLower.includes("homestay") || nameLower.includes("home stay") || nameLower.includes("host family")) {
        return AccommodationType.Homestay;
    }
    if (nameLower.includes("residence") || nameLower.includes("twin") || nameLower.includes("single")) {
        return AccommodationType.Residence;
    }
    return undefined;
}

function adminSchoolCodeForOffering(
    offeringSchoolId: string,
    selectedSchools: SchoolOption[]
): string | undefined {
    const raw = String(offeringSchoolId || "").trim();
    if (!raw) return undefined;
    const numeric = raw.replace(/^School-/i, "").trim();
    const match = selectedSchools.find((school) => {
        const code = String(school.code || "").trim();
        const schoolNumeric = code.replace(/^School-/i, "").trim();
        return code === raw || schoolNumeric === numeric;
    });
    return match?.code;
}

function toIsoDate(value?: string): string | undefined {
    if (!value) return undefined;
    const raw = String(value).trim();
    if (!raw) return undefined;
    const match = raw.match(/^(\d{4}-\d{2}-\d{2})/);
    return match ? match[1] : raw;
}

export type ApplicableProductOfferingsResult = {
    products: Product[];
    offerings: ProductOffering[];
    loadedSchoolCodes: string[];
};

export async function loadApplicableProductOfferings(params: {
    divisionId: number;
    selectedSchools: SchoolOption[];
    productDateFrom?: string;
    productDateTo?: string;
}): Promise<ApplicableProductOfferingsResult> {
    const { divisionId, selectedSchools, productDateFrom, productDateTo } = params;
    const loadedSchoolCodes = selectedSchools.map((school) => school.code).filter(Boolean);
    if (divisionId <= 0 || selectedSchools.length === 0) {
        return { products: [], offerings: [], loadedSchoolCodes };
    }

    const schoolCodes = loadedSchoolCodes;
    if (!schoolCodes.length) {
        return { products: [], offerings: [], loadedSchoolCodes };
    }

    const rawOfferings = await getOfferingsMinimal(schoolCodes, {
        activeOnly: true,
        divisionId,
        dateFrom: toIsoDate(productDateFrom),
        dateTo: toIsoDate(productDateTo),
    });

    const merged = new Map<string, Product>();
    const schoolOfferings: ProductOffering[] = [];
    for (const offering of rawOfferings) {
        const product = productFromOffering(offering);
        if (!product) continue;

        const existing = merged.get(product.code);
        if (!existing) {
            merged.set(product.code, product);
        } else if (!existing.name && product.name) {
            merged.set(product.code, product);
        }

        const adminSchoolCode = adminSchoolCodeForOffering(offering.schoolId, selectedSchools);
        if (adminSchoolCode) {
            schoolOfferings.push({
                schoolId: adminSchoolCode,
                productId: product.code,
                name: product.name,
            });
        }
    }

    return {
        products: [...merged.values()].sort((a, b) => a.name.localeCompare(b.name)),
        offerings: schoolOfferings,
        loadedSchoolCodes,
    };
}
